/* eslint-disable no-bitwise */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox } from '@mui/material';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-toastify';
import { AvatarUser } from './editUserAndRoleSideBarStyles';
import Strings from '../../../i18n';
import { updateUserInfoWithoutPassword } from '../../../utils/validators';
import { UserStatus } from '../../../constants/enums/userStatus';
import { EditUserAndRoleSideBarProps } from './editUserAndRoleSideBarProps';
import { ErrorText } from '../../../components/auth/Profile/completeProfileStyles';
import {
  useDeleteSystemUserMutation,
  usePostUserInfoMutation,
  useUpdateUserInfoMutation,
  useUpdateUserStatusMutation
} from '../../../services/users/usersService';
import {
  useGetSystemRolesMutation,
  useAddUpdateSystemUserRoleMutation,
  useResetLimitThrottlingMutation
} from '../../../services/systemPermissions/systemPermissionsService';
import { selectSystemUsersReadWrite } from '../../../store/slices/systemPermissions/systemPermissions';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledEditablePropertyItemContainer,
  StyledTitleSideBar
} from '../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SelectItemMethod } from '../../../components/commons/SideBarComponents/SelectItemMethod/selectItemMethod';
import { SideBarEditablePropertyItem } from '../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { SideBarDeleteActionWithPopup } from '../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import { SideBarReadOnly } from '../../../components/commons/SideBarComponents/ReadOnlyItems/sideBarReadOnly';
import { stringAvatar } from '../../../utils/users';
import { LoadingRowsComponent } from '../../../components/commons/LoadingRows/LoadingRows';
import { SideBarSimpleMethodItem } from '../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { viewRightSideBarEnum } from '../../../constants/sidebarEnums';
import { SideBarPhoneNumber } from '../../../components/commons/SideBarComponents/PhoneNumberWithLada/sideBarPhoneNumber';
import { userRole } from '../../../constants/commonEnums';

export const EditUserAndRoleSideBar = ({
  user,
  reloadTable,
  setOpenSideBar,
  setUsersSelected,
  openRightSideBarForMethod
}: EditUserAndRoleSideBarProps) => {
  const navigate = useNavigate();
  const permissionReadWrite = useSelector(selectSystemUsersReadWrite);

  const [dataRolesWithAssignLater, setDataRolesWithAssignLater] = useState<
    { id: any; name: any }[]
  >([]);
  const [roleId, setRoleId] = React.useState(user?.systemRoleId);
  const [checked, setChecked] = useState(user?.systemRoleId === userRole.admin);
  const handleChangeRole = (event: any) => {
    setRoleId(+event.target.value);
  };
  const handleChangeCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const [
    postInfo,
    { isLoading: isLoadingPost, isSuccess: isSuccessPost, data: dataPost }
  ] = usePostUserInfoMutation();

  const [putInfo, { isLoading: isLoadingPut, isSuccess: isSuccessPut }] =
    useUpdateUserInfoMutation();

  const [
    deleteUserById,
    { isLoading: isLoadingDelete, isSuccess: isSuccessDelete }
  ] = useDeleteSystemUserMutation();

  const [
    getSystemRoles,
    {
      isSuccess: successGetSystemRoles,
      isLoading: isLoadingGetSystemRoles,
      data: dataSystemRoles
    }
  ] = useGetSystemRolesMutation();

  const [
    resetLimit,
    { isSuccess: successResetLimit, isLoading: isLoadingResetLimit }
  ] = useResetLimitThrottlingMutation();

  const [
    updateRoleByUserId,
    { isSuccess: successUpdateRole, data: dataUpdateRole }
  ] = useAddUpdateSystemUserRoleMutation();

  const [updateUser, { isSuccess: successUpdate }] =
    useUpdateUserStatusMutation();
  const updateUserRole = () => {
    const payload = {
      userId: user?.userId,
      systemRoleId: roleId
    };
    updateRoleByUserId(payload);
  };
  const sendInfo = (payload: any) => {
    if (user?.systemRoleId !== roleId) {
      updateUserRole();
    }
    if (
      user?.firstName !== payload.firstName ||
      user?.lastName !== payload.lastName ||
      user?.phoneNumber !== payload.phoneNumber ||
      user?.email !== payload.email
    ) {
      putInfo(payload);
    }
  };

  const deleteUser = () => {
    deleteUserById(user?.userId);
  };

  useEffect(() => {
    getSystemRoles('');
    setRoleId(user?.systemRoleId);
  }, []);

  useEffect(() => {
    if (successGetSystemRoles && dataSystemRoles) {
      const roles = [
        { id: 0, name: Strings.common.assignLater },
        ...dataSystemRoles
      ];
      setDataRolesWithAssignLater(roles);
    }
  }, [successGetSystemRoles]);

  useEffect(() => {
    if (isSuccessDelete) {
      toast.success(Strings.userProfile.successDelete);
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccessDelete]);

  useEffect(() => {
    if (successUpdate) {
      toast.success(Strings.userProfile.successActivate);
      reloadTable();
      setOpenSideBar(false);
    }
  }, [successUpdate]);

  useEffect(() => {
    if (isSuccessPut) {
      toast.success(Strings.userProfile.successEdited);
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccessPut]);

  useEffect(() => {
    if (isSuccessPost) {
      toast.success(Strings.userProfile.successEdited);
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccessPost]);

  useEffect(() => {
    if (successUpdateRole) {
      toast.success(Strings.users.successChangeRole);
      reloadTable();
      setOpenSideBar(false);
    }
  }, [successUpdateRole]);

  const getDisabledButton = (
    values: {
      firstName: string;
      lastName: string;
      phoneNumber: string | number;
      email: string;
    },
    isValid: boolean
  ) => {
    if (!isValid) {
      return true;
    }
    return false;
  };

  const redirectToSeeNotes = () => {
    navigate('/my-notes', {
      state: {
        type: 'Users',
        id: user?.userId
      }
    });
  };

  return (
    <>
      {user && !isLoadingGetSystemRoles && (
        <Formik
          enableReinitialize
          validationSchema={updateUserInfoWithoutPassword}
          initialValues={{
            email: user?.email,
            firstName: user.firstName,
            lastName: user.lastName,
            phoneNumber: user.phoneNumber
          }}
          onSubmit={(values) => {
            if (checked) {
              updateRoleByUserId({
                userId: user.userId,
                systemRoleId: userRole.admin
              });
            }

            sendInfo({
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              phoneNumber: values.phoneNumber,
              userId: user.userId
            });
          }}
        >
          {({
            handleChange,
            handleSubmit,
            values,
            isValid,
            setFieldTouched,
            errors,
            touched
          }) => (
            <>
              <StyledContainerItems data-testid="edit-user-and-role-sidebar">
                <StyledTitleSideBar>
                  {Strings.users.userDetails}
                </StyledTitleSideBar>

                {permissionReadWrite && (
                  <>
                    <SideBarReadOnly
                      readOnlyItems={[
                        {
                          name: Strings.userProfile.fullNameLabel,
                          element: (
                            <AvatarUser
                              alt="user"
                              {...stringAvatar(
                                `${user.firstName} ${user.lastName}`
                              )}
                            />
                          ),
                          value: `${user?.firstName} ${user?.lastName}`
                        },
                        {
                          name: Strings.userProfile.emailLabel,
                          value: user?.email
                        },
                        {
                          name: Strings.userProfile.statusLabel,
                          value: user?.hasAuth0Account
                            ? Strings.users.active
                            : Strings.users.pending
                        }
                      ]}
                    />
                    <SideBarEditablePropertyItem
                      item={{
                        label: Strings.userProfile.firstNameLabel,
                        type: 'inputText',
                        currentValue: values.firstName,
                        nameAndId: 'firstName',
                        onChange: handleChange('firstName'),
                        onBlur: () => setFieldTouched('firstName')
                      }}
                    />
                    {touched.firstName && errors.firstName && (
                      <ErrorText>{errors.firstName}</ErrorText>
                    )}
                    <SideBarEditablePropertyItem
                      item={{
                        label: Strings.userProfile.lastNameLabel,
                        type: 'inputText',
                        currentValue: values.lastName,
                        nameAndId: 'lastName',
                        onChange: handleChange('lastName'),
                        onBlur: () => setFieldTouched('lastName')
                      }}
                    />
                    {touched.lastName && errors.lastName && (
                      <ErrorText>{errors.lastName}</ErrorText>
                    )}
                    <SideBarPhoneNumber
                      value={values.phoneNumber}
                      handleChange={(value) =>
                        handleChange('phoneNumber')(value ?? '')
                      }
                      handleBlur={() => setFieldTouched('phoneNumber')}
                      id="phoneNumber"
                    />
                    {touched.phoneNumber && errors.phoneNumber && (
                      <ErrorText>{errors.phoneNumber}</ErrorText>
                    )}
                    <SelectItemMethod
                      item={{
                        label: Strings.users.systemRole,
                        currentValue: roleId ?? user.systemRoleId,
                        options: dataRolesWithAssignLater || [],
                        onChange: handleChangeRole,
                        placeholder: Strings.devices.productType
                      }}
                    />
                    <StyledEditablePropertyItemContainer>
                      <div className="label">
                        {Strings.users.fullAccessServer}
                      </div>
                      <Checkbox
                        onBlur={() => setFieldTouched('fullAccess')}
                        checked={checked}
                        onChange={(e) => {
                          handleChange('fullAccess')(`${checked}`);
                          handleChangeCheck(e);
                        }}
                        color="secondary"
                      />
                    </StyledEditablePropertyItemContainer>
                    <SideBarSimpleMethodItem
                      icon={
                        <CheckCircleOutlineIcon
                          style={{ marginRight: 5 }}
                          color="action"
                        />
                      }
                      name={Strings.users.forceActivation}
                      action={() => {
                        updateUser({
                          email: user.email,
                          status: UserStatus.activated
                        });
                      }}
                      successAction={false}
                      textSuccess="success"
                    />
                    <SideBarSimpleMethodItem
                      name={Strings.users.resetLimit}
                      action={() => {
                        resetLimit(user.userId);
                      }}
                      loading={isLoadingResetLimit}
                      successAction={successResetLimit}
                      textSuccess={Strings.users.resetLimitSuccess}
                    />
                    <SideBarSimpleMethodItem
                      name={Strings.notes.addNote}
                      action={() => {
                        openRightSideBarForMethod(viewRightSideBarEnum.addNote);
                      }}
                      successAction={false}
                      textSuccess="success"
                    />

                    <SideBarSimpleMethodItem
                      name={Strings.navBar.viewApplicationsLogs}
                      action={() => {
                        navigate(`/applications-logs?userId=${user.userId}`, {
                          state: {
                            userId: user.userId,
                            userName: `${user.firstName} ${user.lastName}`
                          }
                        });
                      }}
                      successAction={false}
                      textSuccess="success"
                    />
                    {user.hasNotes && (
                      <SideBarSimpleMethodItem
                        name={Strings.notes.seeNotes}
                        action={redirectToSeeNotes}
                        successAction={false}
                        textSuccess="success"
                      />
                    )}
                    <SideBarSimpleMethodItem
                      name={Strings.users.viewLocations}
                      action={() => {
                        navigate(`/user/${user.userId}/locations`);
                      }}
                      successAction={false}
                      textSuccess="success"
                    />
                    <SideBarSimpleMethodItem
                      name={Strings.users.locationsShared}
                      action={() => {
                        navigate(
                          `/user/${user.userId}/locations?filterByShared=true`
                        );
                      }}
                      successAction={false}
                      textSuccess="success"
                    />
                    <SideBarSimpleMethodItem
                      name={Strings.users.assignDefaultLoc}
                      action={() => {
                        openRightSideBarForMethod(
                          viewRightSideBarEnum.addUserToDefaultLocation
                        );
                      }}
                      successAction={false}
                      textSuccess="success"
                    />
                    {permissionReadWrite && (
                      <SideBarSimpleMethodItem
                        name={Strings.userProfile.changePassword}
                        action={() => {
                          openRightSideBarForMethod(
                            viewRightSideBarEnum.changeUserPassword
                          );
                        }}
                        successAction={false}
                        textSuccess="success"
                      />
                    )}
                    <SideBarDeleteActionWithPopup
                      id={user.userId}
                      onDelete={() => deleteUser()}
                      titleConfirmation={Strings.users.deleteUserTitle}
                      textConfirmation={Strings.common.thisActionIsIrreversible}
                      textIcon={Strings.userProfile.deleteUserLabel}
                      showLocations
                    />
                  </>
                )}
              </StyledContainerItems>
              <StyledContainerFooterSideBar>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setOpenSideBar(false);
                    setUsersSelected([]);
                  }}
                >
                  {Strings.common.cancel}
                </button>
                <Button
                  variant="contained"
                  size="large"
                  className="btn-save"
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {Strings.common.save}
                </Button>
              </StyledContainerFooterSideBar>
            </>
          )}
        </Formik>
      )}
      {isLoadingGetSystemRoles && <LoadingRowsComponent />}
    </>
  );
};
